import { MedplumClient } from '@medplum/core';
import { EpisodeOfCare } from '@medplum/fhirtypes';
import { BENEFIT_ADMINISTRATOR_CODESYSTEM, BenefitAdministrator, Track, TRACK_CODESYSTEM } from './constants';

const TRACK_VALUES = new Set<string>(Object.values(Track));

const ADMINISTRATOR_VALUES = new Set<string>(Object.values(BenefitAdministrator));

/** Administrator → the Track it collapses to for display, and the track a partner match switches to. */
export const TRACK_BY_ADMINISTRATOR: Record<BenefitAdministrator, Track> = {
  [BenefitAdministrator.CARRUM]: Track.CARRUM,
  [BenefitAdministrator.TRANSCARENT]: Track.TRANSCARENT,
};

/**
 * Reads the benefit-administrator coding carried by an episode, when it has
 * one. Absent means the employer benefit is claimed but unattributed — or that
 * the episode predates BAR-2602, where the administrator WAS the track.
 */
export function getBenefitAdministratorFromEpisodeOfCare(episode: EpisodeOfCare): BenefitAdministrator | undefined {
  for (const concept of episode.type ?? []) {
    for (const coding of concept.coding ?? []) {
      if (coding.system === BENEFIT_ADMINISTRATOR_CODESYSTEM && coding.code && ADMINISTRATOR_VALUES.has(coding.code)) {
        return coding.code as BenefitAdministrator;
      }
    }
  }
  return undefined;
}

/**
 * The patient's track, reading no episode — or no track on it — as self-pay:
 * production writes an episode only for employer tracks (BAR-2941).
 */
export function getTrackOrSelfPay(episode: EpisodeOfCare | undefined): Track {
  return (episode ? getTrackFromEpisodeOfCare(episode) : undefined) ?? Track.D2C;
}

/**
 * Reads the track (EpisodeOfCare.type, from the track CodeSystem) carried by an
 * episode. Returns undefined when the episode has no recognised track coding.
 */
export function getTrackFromEpisodeOfCare(episode: EpisodeOfCare): Track | undefined {
  // BAR-2602 splits the administrator onto its own coding and sets the track to
  // `employer`. Orbit keeps the collapsed vocabulary: the administrator outranks
  // the track coding, so a new `employer` + `carrum` episode and a legacy
  // `carrum` one both read as Track.CARRUM and every consumer stays unchanged.
  const administrator = getBenefitAdministratorFromEpisodeOfCare(episode);
  if (administrator) {
    return TRACK_BY_ADMINISTRATOR[administrator];
  }

  for (const concept of episode.type ?? []) {
    for (const coding of concept.coding ?? []) {
      if (coding.system === TRACK_CODESYSTEM && coding.code && TRACK_VALUES.has(coding.code)) {
        return coding.code as Track;
      }
    }
  }
  return undefined;
}

/**
 * The episode's track in bariendo-be's vocabulary, uncollapsed: every partner
 * patient is `employer`, and who administers the benefit is a separate field.
 * A legacy pre-BAR-2602 episode whose track coding IS the administrator reads
 * the same way, the way bariendo-be's `normalizeLegacyTrack` does. Customer.io
 * shares one profile with bariendo-be, so it must see this rather than the
 * collapsed `getTrackFromEpisodeOfCare` value, or Orbit overwrites `employer`.
 */
export function getTrackPairFromEpisodeOfCare(
  episode: EpisodeOfCare
): { track: Track; administrator: BenefitAdministrator | null } | undefined {
  let recorded: Track | undefined;
  for (const concept of episode.type ?? []) {
    for (const coding of concept.coding ?? []) {
      if (!recorded && coding.system === TRACK_CODESYSTEM && coding.code && TRACK_VALUES.has(coding.code)) {
        recorded = coding.code as Track;
      }
    }
  }
  const legacyAdministrator =
    recorded && ADMINISTRATOR_VALUES.has(recorded) ? (recorded as string as BenefitAdministrator) : undefined;
  const administrator = getBenefitAdministratorFromEpisodeOfCare(episode) ?? legacyAdministrator ?? null;
  if (administrator) {
    return { track: Track.EMPLOYER, administrator };
  }
  return recorded ? { track: recorded, administrator: null } : undefined;
}

/**
 * Resolves a patient's currently-open EpisodeOfCare.
 *
 * The track model is append-only: switching tracks closes the old episode and
 * opens a new one, so a patient has at most one `active` episode at a time. This
 * reads that currently-open episode, returning undefined when there is none.
 */
export async function resolveActiveEpisodeOfCare(
  medplum: MedplumClient,
  patientId: string
): Promise<EpisodeOfCare | undefined> {
  const episodes = await medplum.searchResources('EpisodeOfCare', {
    patient: `Patient/${patientId}`,
    status: 'active',
  });
  return episodes[0];
}

/**
 * Resolves a patient's track from their currently-open EpisodeOfCare. Returns
 * undefined when the patient has no active episode or it carries no track.
 */
export async function getPatientTrack(medplum: MedplumClient, patientId: string): Promise<Track | undefined> {
  const episode = await resolveActiveEpisodeOfCare(medplum, patientId);
  return episode ? getTrackFromEpisodeOfCare(episode) : undefined;
}
